/**
 * VoiceControls — state + UI pieces for voice mode, glide, unison, spread.
 *
 * Historically this owned its own rack panel with sliders. The rack was
 * restructured to merge OSC + Voice + Noise into one SOURCE section, so
 * VoiceControls now exposes the UI pieces (`modeGroup`, `glideKnob`,
 * `unisonKnob`, `spreadKnob`) for the composing panel to place, and keeps
 * the state plumbing that wires Parameters into the VoiceManager.
 *
 * Still exposes the `.element` (a full module-panel) so code paths that
 * want the self-contained panel layout keep working — but the rack no
 * longer appends it. The knobs inside `.element` are the same instances
 * as the public `glideKnob` etc. so adding it to the DOM moves them.
 */

import type { VoiceManager, VoiceMode } from '@ac-slayer/synth-core';
import { NumericParameter } from '@ac-slayer/synth-core';
import { Knob } from '../components/Knob';

export type VoiceControlsSettings = {
  readonly mode: VoiceMode;
  readonly glideMs: number;
};

export interface VoiceControlsOptions {
  /** Getter is called lazily because the VoiceManager is created on audio unlock. */
  readonly getVoices: () => VoiceManager | null;
  readonly initial?: Partial<VoiceControlsSettings>;
  /**
   * Is POLY mode useful right now? Returns false when no input source can
   * produce chords (e.g., only QWERTY connected — limited n-key rollover
   * makes POLY indistinguishable from MONO for the user). When false, the
   * POLY button is hidden and the active mode falls back to MONO.
   */
  readonly isPolyAvailable?: () => boolean;
  /** Subscribe to availability changes so the panel can re-render live. */
  readonly onPolyAvailabilityChange?: (listener: () => void) => () => void;
}

const MODES: readonly VoiceMode[] = ['poly', 'mono', 'legato'] as const;

const MODE_LABEL: Record<VoiceMode, string> = {
  poly: 'POLY',
  mono: 'MONO',
  legato: 'LEG',
};

const MODE_TITLE: Record<VoiceMode, string> = {
  poly: 'Polyphonic — up to 8 simultaneous notes. Needs MIDI or multi-touch; QWERTY has limited n-key rollover.',
  mono: 'Monophonic — one voice; each new note retriggers the envelope. Pair with Glide for slide / portamento.',
  legato: 'Legato — one voice; overlapping notes slur without retriggering. Pair with Glide for smooth phrasing.',
};

export class VoiceControls {
  readonly element: HTMLElement;

  /**
   * Pieces exposed for external composition (e.g. embedding inside the
   * SOURCE panel). Appending the full `.element` to the DOM moves these
   * together; composing with just the pieces lets the caller lay them
   * out alongside other module content.
   *
   * `modeControl` is the outer wrapper with the "Mode" label on top of
   * the POLY / MONO / LEG buttons; `modeGroup` is the inner button row
   * (kept exposed for callers that want just the buttons).
   */
  readonly modeControl: HTMLElement;
  readonly modeGroup: HTMLElement;
  readonly glideKnob: Knob;
  readonly unisonKnob: Knob;
  readonly spreadKnob: Knob;

  readonly glide: NumericParameter;
  readonly unison: NumericParameter;
  readonly spread: NumericParameter;

  private mode: VoiceMode;
  private readonly getVoices: () => VoiceManager | null;
  private readonly opts: VoiceControlsOptions;
  private unsubscribePolyAvailability: (() => void) | null = null;

  private readonly modeButtons = new Map<VoiceMode, HTMLButtonElement>();

  constructor(opts: VoiceControlsOptions) {
    this.opts = opts;
    this.getVoices = opts.getVoices;
    this.mode = opts.initial?.mode ?? 'poly';

    this.glide = new NumericParameter({
      name: 'Glide',
      default: opts.initial?.glideMs ?? 0,
      min: 0,
      max: 1000,
      curve: 'linear',
      smoothingMs: 0,
      randomSkew: 0.7,
      formatter: (v) =>
        v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`,
    });

    this.unison = new NumericParameter({
      name: 'Unison',
      default: 1,
      min: 1,
      max: 7,
      curve: 'linear',
      smoothingMs: 0,
      randomSkew: 0.5,
      formatter: (v) => `${Math.round(v)}`,
    });

    this.spread = new NumericParameter({
      name: 'Spread',
      default: 15,
      min: 0,
      max: 100,
      curve: 'linear',
      smoothingMs: 0,
      unit: 'ct',
      formatter: (v) => `${Math.round(v)}`,
    });

    if (this.mode === 'poly' && !this.isPolyAvailable()) this.mode = 'mono';

    // ── Frame (optional — the rack may not add this element anymore;
    // it still works as a standalone panel if anything wants it).
    this.element = document.createElement('section');
    this.element.className = 'module-panel voice-controls';
    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-label', 'Voice controls');

    const header = document.createElement('header');
    header.className = 'module-panel-header';
    const title = document.createElement('h2');
    title.className = 'module-panel-title';
    title.textContent = 'VOICE';
    header.appendChild(title);
    this.element.appendChild(header);

    const body = document.createElement('div');
    body.className = 'module-panel-content';
    this.element.appendChild(body);

    // ── Mode — a wave-select-style segmented group for POLY / MONO / LEG.
    // `modeControl` wraps the "Mode" label + button row together, so
    // embedding it in another panel automatically includes the label.
    this.modeControl = document.createElement('div');
    this.modeControl.className = 'wave-select';
    const modeLabel = document.createElement('div');
    modeLabel.className = 'label';
    modeLabel.textContent = 'Mode';
    this.modeControl.appendChild(modeLabel);

    this.modeGroup = document.createElement('div');
    this.modeGroup.className = 'wave-select-group';
    this.modeGroup.setAttribute('role', 'radiogroup');
    this.modeGroup.setAttribute('aria-label', 'Voice mode');
    for (const m of MODES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wave-select-btn';
      btn.textContent = MODE_LABEL[m];
      btn.title = MODE_TITLE[m];
      btn.setAttribute('role', 'radio');
      btn.setAttribute('data-value', m);
      btn.addEventListener('click', () => this.setMode(m));
      this.modeGroup.appendChild(btn);
      this.modeButtons.set(m, btn);
    }
    this.modeControl.appendChild(this.modeGroup);
    body.appendChild(this.modeControl);

    // ── Knobs — one per voice param. Knob components self-render from
    // their Parameter, so the subscribe handlers below only need to push
    // changes into the VoiceManager, not update the UI.
    this.glideKnob = new Knob({ param: this.glide });
    this.unisonKnob = new Knob({ param: this.unison });
    this.spreadKnob = new Knob({ param: this.spread });
    body.appendChild(this.glideKnob.element);
    body.appendChild(this.unisonKnob.element);
    body.appendChild(this.spreadKnob.element);

    // ── State → voice-manager plumbing. These subscribe fires on user
    // drags, preset loads, and randomize rolls — same chain as other
    // NumericParameters in the app.
    this.glide.subscribe((v) => this.getVoices()?.setGlideMs(v));
    this.unison.subscribe((v) => this.getVoices()?.setUnison(Math.round(v)));
    this.spread.subscribe((v) => this.getVoices()?.setUnisonSpread(v));

    this.renderMode();

    if (opts.onPolyAvailabilityChange) {
      this.unsubscribePolyAvailability = opts.onPolyAvailabilityChange(() =>
        this.refreshPolyAvailability(),
      );
    }
  }

  dispose(): void {
    this.unsubscribePolyAvailability?.();
    this.unsubscribePolyAvailability = null;
    this.glideKnob.dispose();
    this.unisonKnob.dispose();
    this.spreadKnob.dispose();
  }

  private isPolyAvailable(): boolean {
    return this.opts.isPolyAvailable?.() ?? true;
  }

  private refreshPolyAvailability(): void {
    const polyAvailable = this.isPolyAvailable();
    const polyBtn = this.modeButtons.get('poly');
    if (polyBtn) polyBtn.hidden = !polyAvailable;
    if (!polyAvailable && this.mode === 'poly') this.setMode('mono');
  }

  /** Current cached mode. Useful when the voice pool is rebuilt on audio unlock. */
  getMode(): VoiceMode {
    return this.mode;
  }

  getGlideMs(): number {
    return this.glide.value;
  }

  setMode(mode: VoiceMode): void {
    if (mode === this.mode) return;
    this.mode = mode;
    this.getVoices()?.setMode(mode);
    this.renderMode();
  }

  setGlideMs(ms: number): void {
    this.glide.set(ms, 'ui');
  }

  private renderMode(): void {
    const polyAvailable = this.isPolyAvailable();
    for (const [m, btn] of this.modeButtons) {
      const active = m === this.mode;
      btn.classList.toggle('active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
      btn.tabIndex = active ? 0 : -1;
      if (m === 'poly') btn.hidden = !polyAvailable;
    }
  }
}
